import { useMemo, useState, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';
import {
    IpxCredentialFieldClassification,
    IpxCredentialScopeType,
    type IpxCredentialFieldDescriptor,
    type IpxCredentialSaveFields,
    type IpxCredentialScope,
    type IpxCredentialSummaryListItem,
    type ListIpxCredentialsForPolicyResponse,
    type RevealIpxCredentialSecretFieldResponse,
} from '@pillar-portal/api-types';
import { Status } from '@pillar-portal/database/dist/frontend';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { StatusBadge } from '@/components/ui/status-badge';
import { ipxCredentialManagementApi } from '@/lib/pillarApi';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { useAuthStore } from '@/store/auth';
import {
    formatIpxCredentialPolicyKey,
    getIpxCredentialPolicySystemDisplayName,
} from './OrganizationIpxCredentialPoliciesCard';
import { IpxCredentialFieldDisplayList } from './IpxCredentialFieldDisplayList';
import { IpxCredentialForm, type IpxCredentialFormInitialValues } from './IpxCredentialForm';
import { IpxCredentialRotationButton } from './IpxCredentialRotationButton';
import { IpxCredentialHistoryButton } from './IpxCredentialHistoryButton';
import { IpxCredentialRecoveryNotice } from './IpxCredentialRecoveryNotice';
import { validateIpxCredentialAccountId } from './ipxCredentialAccountIdValidation';

export const IpxCredentialPolicyDetailOrganizationUuidParamSchema: z.ZodType<string> = z
    .string()
    .uuid();
export const IpxCredentialPolicyDetailPolicyUuidParamSchema: z.ZodType<string> = z.string().uuid();

function getErrorMessage(error: unknown, fallbackMessage: string): string {
    return error instanceof Error ? error.message : fallbackMessage;
}

type CredentialLifecycleAction = 'disable' | 'enable' | 'delete' | 'restore';

function renderCredentialScope(credential: IpxCredentialSummaryListItem): ReactElement {
    if (credential.scope.scope_type === IpxCredentialScopeType.ORGANIZATION) {
        return <span>Organization credential</span>;
    }
    if (credential.owner !== null && credential.owner !== undefined) {
        return (
            <Link
                className="text-primary underline underline-offset-2"
                to={`/users/${credential.owner.user_uuid}`}
            >
                {credential.owner.label}
            </Link>
        );
    }
    return <span>Personal credential</span>;
}

function getCredentialRoleAssignmentUuid(credential: IpxCredentialSummaryListItem): string | null {
    return credential.scope.scope_type === IpxCredentialScopeType.ROLE_ASSIGNMENT
        ? credential.scope.role_assignment_uuid
        : null;
}

function buildFirstCredentialUuidByRoleAssignment(
    credentials: readonly IpxCredentialSummaryListItem[]
): ReadonlyMap<string, string> {
    const firstCredentialUuidByRoleAssignment = new Map<string, string>();
    for (const credential of credentials) {
        const roleAssignmentUuid = getCredentialRoleAssignmentUuid(credential);
        if (
            roleAssignmentUuid !== null &&
            !firstCredentialUuidByRoleAssignment.has(roleAssignmentUuid)
        ) {
            firstCredentialUuidByRoleAssignment.set(roleAssignmentUuid, credential.credential_uuid);
        }
    }
    return firstCredentialUuidByRoleAssignment;
}

function listCredentialsForRoleAssignment(
    credentials: readonly IpxCredentialSummaryListItem[],
    roleAssignmentUuid: string
): readonly IpxCredentialSummaryListItem[] {
    return credentials.filter(
        (credential) => getCredentialRoleAssignmentUuid(credential) === roleAssignmentUuid
    );
}

function renderFieldDescriptor(descriptor: IpxCredentialFieldDescriptor): ReactElement {
    const classificationLabel =
        descriptor.field_classification === IpxCredentialFieldClassification.SECRET
            ? 'Secret'
            : 'Public';
    return (
        <div
            key={descriptor.field_name}
            className="border rounded p-3 flex items-center justify-between gap-3"
        >
            <div>
                <div className="font-medium">{descriptor.display.label}</div>
                <div className="text-sm text-muted-foreground">{descriptor.field_name}</div>
            </div>
            <div className="flex items-center gap-2">
                <Badge variant="outline">{classificationLabel}</Badge>
                {descriptor.required ? <Badge variant="secondary">Required</Badge> : null}
            </div>
        </div>
    );
}

function renderCredentialFieldDisplayList(
    credential: IpxCredentialSummaryListItem,
    fieldDescriptors: readonly IpxCredentialFieldDescriptor[]
): ReactElement {
    const credentialFieldDescriptors: readonly IpxCredentialFieldDescriptor[] =
        credential.field_descriptors.length === 0 ? fieldDescriptors : credential.field_descriptors;
    return (
        <IpxCredentialFieldDisplayList
            fieldDescriptors={credentialFieldDescriptors}
            publicFields={credential.capabilities.can_display ? credential.public_fields : {}}
            canRevealSecretFields={credential.capabilities.can_reveal_secret_fields}
            revealSecretFieldsDisabledReason={
                credential.capabilities.reveal_secret_fields_disabled_reason
            }
            onRevealSecretField={(
                fieldName: string
            ): Promise<RevealIpxCredentialSecretFieldResponse> =>
                ipxCredentialManagementApi.revealPolicyCredentialSecretField({
                    credential_uuid: credential.credential_uuid,
                    field_name: fieldName,
                })
            }
        />
    );
}

function getCredentialFieldDescriptors(
    credential: IpxCredentialSummaryListItem,
    fieldDescriptors: readonly IpxCredentialFieldDescriptor[]
): readonly IpxCredentialFieldDescriptor[] {
    return credential.field_descriptors.length === 0
        ? fieldDescriptors
        : credential.field_descriptors;
}

function buildCredentialFormInitialValues(
    credential: IpxCredentialSummaryListItem,
    fieldDescriptors: readonly IpxCredentialFieldDescriptor[]
): IpxCredentialFormInitialValues {
    if (!credential.capabilities.can_display) {
        return {
            account_id: credential.account_id,
            field_values: {},
        };
    }
    const editableFieldValues: Record<string, string> = {};
    for (const descriptor of getCredentialFieldDescriptors(credential, fieldDescriptors)) {
        if (descriptor.field_classification === IpxCredentialFieldClassification.PUBLIC) {
            editableFieldValues[descriptor.field_name] =
                credential.public_fields[descriptor.field_name] ?? '';
        }
    }
    return {
        account_id: credential.account_id,
        field_values: editableFieldValues,
    };
}

function getCredentialLifecycleCapability(
    credential: IpxCredentialSummaryListItem,
    action: CredentialLifecycleAction
): boolean {
    switch (action) {
        case 'disable':
            return credential.capabilities.can_disable;
        case 'enable':
            return credential.capabilities.can_enable;
        case 'delete':
            return credential.capabilities.can_delete;
        case 'restore':
            return credential.capabilities.can_restore;
    }
}

function getCredentialLifecycleDisabledReason(action: CredentialLifecycleAction): string {
    switch (action) {
        case 'disable':
            return 'You do not have permission to disable this credential.';
        case 'enable':
            return 'You do not have permission to enable this credential.';
        case 'delete':
            return 'You do not have permission to delete this credential.';
        case 'restore':
            return 'You do not have permission to restore this credential.';
    }
}

function listCredentialLifecycleActions(
    credential: IpxCredentialSummaryListItem
): readonly CredentialLifecycleAction[] {
    switch (credential.status) {
        case Status.ACTIVE:
            return ['disable', 'delete'];
        case Status.DISABLED:
            return ['enable', 'delete'];
        case Status.DELETED:
            return ['restore'];
        default:
            return [];
    }
}

function getCredentialLifecycleButtonLabel(action: CredentialLifecycleAction): string {
    switch (action) {
        case 'disable':
            return 'Disable';
        case 'enable':
            return 'Enable';
        case 'delete':
            return 'Delete';
        case 'restore':
            return 'Restore';
    }
}

function renderCredentialRotationControls(params: {
    readonly credential: IpxCredentialSummaryListItem;
    readonly organizationUuid: string;
    readonly policyUuid: string;
}): ReactElement {
    const historyPath = `/organizations/${params.organizationUuid}/credential-policies/${params.policyUuid}/credentials/${params.credential.credential_uuid}/rotation-history`;
    const recoverySummary = params.credential.rotation_eligibility?.recovery_summary ?? null;
    const activeAttempt = params.credential.rotation_eligibility?.active_attempt ?? null;
    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
                <IpxCredentialRotationButton
                    canRotate={params.credential.capabilities.can_rotate}
                    disabledReason={params.credential.capabilities.rotate_disabled_reason}
                />
                <IpxCredentialHistoryButton
                    canViewHistory={params.credential.capabilities.can_view_history}
                    disabledReason={params.credential.capabilities.history_disabled_reason}
                    historyPath={historyPath}
                />
            </div>
            <IpxCredentialRecoveryNotice recoverySummary={recoverySummary} />
            {activeAttempt === null ? null : (
                <div className="text-sm text-muted-foreground">
                    Active rotation attempt: {activeAttempt.state}
                </div>
            )}
        </div>
    );
}

export function IpxCredentialPolicyDetailPage(): ReactElement {
    const queryClient = useQueryClient();
    const [editingCredentialUuid, setEditingCredentialUuid] = useState<string | null>(null);
    const [creatingCredentialScope, setCreatingCredentialScope] =
        useState<IpxCredentialScope | null>(null);
    const params = useParams();
    const organizationUuid: string = IpxCredentialPolicyDetailOrganizationUuidParamSchema.parse(
        params.id
    );
    const policyUuid: string = IpxCredentialPolicyDetailPolicyUuidParamSchema.parse(
        params.policyUuid
    );
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const queryKey = useMemo(
        () =>
            buildRoleScopedQueryKey(activeRoleAssignmentId, [
                'ipx-credential-policy-detail',
                policyUuid,
            ]),
        [activeRoleAssignmentId, policyUuid]
    );
    const detailQuery = useQuery<ListIpxCredentialsForPolicyResponse>({
        queryKey,
        queryFn: async () => await ipxCredentialManagementApi.listCredentialsForPolicy(policyUuid),
    });
    const updateCredentialMutation = useMutation({
        mutationFn: async (variables: {
            readonly credentialUuid: string;
            readonly payload: IpxCredentialSaveFields;
        }): Promise<IpxCredentialSummaryListItem> =>
            await ipxCredentialManagementApi.updatePolicyCredential({
                credential_uuid: variables.credentialUuid,
                account_id: variables.payload.account_id,
                edited_fields: variables.payload.edited_fields,
            }),
        onSuccess: async () => {
            setEditingCredentialUuid(null);
            await queryClient.invalidateQueries({ queryKey });
        },
    });
    const createCredentialMutation = useMutation({
        mutationFn: async (variables: {
            readonly scope: IpxCredentialScope;
            readonly payload: IpxCredentialSaveFields;
        }): Promise<IpxCredentialSummaryListItem> =>
            await ipxCredentialManagementApi.createCredentialForPolicy(policyUuid, {
                scope: variables.scope,
                account_id: variables.payload.account_id,
                edited_fields: variables.payload.edited_fields,
            }),
        onSuccess: async () => {
            setCreatingCredentialScope(null);
            await queryClient.invalidateQueries({ queryKey });
        },
    });
    const lifecycleMutation = useMutation({
        mutationFn: async (variables: {
            readonly action: CredentialLifecycleAction;
            readonly credentialUuid: string;
        }): Promise<IpxCredentialSummaryListItem> => {
            switch (variables.action) {
                case 'disable':
                    return await ipxCredentialManagementApi.disablePolicyCredential(
                        variables.credentialUuid
                    );
                case 'enable':
                    return await ipxCredentialManagementApi.enablePolicyCredential(
                        variables.credentialUuid
                    );
                case 'delete':
                    return await ipxCredentialManagementApi.deletePolicyCredential(
                        variables.credentialUuid
                    );
                case 'restore':
                    return await ipxCredentialManagementApi.restorePolicyCredential(
                        variables.credentialUuid
                    );
            }
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey });
        },
    });
    const detail: ListIpxCredentialsForPolicyResponse | undefined = detailQuery.data;
    const creatingRoleAssignmentUuid: string | null =
        creatingCredentialScope?.scope_type === IpxCredentialScopeType.ROLE_ASSIGNMENT
            ? creatingCredentialScope.role_assignment_uuid
            : null;
    const isCreatingOrganizationCredential: boolean =
        creatingCredentialScope?.scope_type === IpxCredentialScopeType.ORGANIZATION;
    const firstCredentialUuidByRoleAssignment = useMemo(
        () =>
            detail === undefined
                ? new Map<string, string>()
                : buildFirstCredentialUuidByRoleAssignment(detail.credentials),
        [detail]
    );

    return (
        <div className="container mx-auto py-6 space-y-6">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold">Credential Policy</h1>
                    {detail !== undefined ? (
                        <p className="text-sm text-muted-foreground">
                            {detail.policy.ipx_credential_definition_name}
                        </p>
                    ) : null}
                </div>
                <Button asChild variant="outline">
                    <Link to={`/organizations/${organizationUuid}`}>Back to Organization</Link>
                </Button>
            </div>

            {detailQuery.error ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {getErrorMessage(detailQuery.error, 'Failed to load credential policy')}
                    </AlertDescription>
                </Alert>
            ) : null}
            {updateCredentialMutation.error ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {getErrorMessage(
                            updateCredentialMutation.error,
                            'Failed to update credential'
                        )}
                    </AlertDescription>
                </Alert>
            ) : null}
            {createCredentialMutation.error ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {getErrorMessage(
                            createCredentialMutation.error,
                            'Failed to create credential'
                        )}
                    </AlertDescription>
                </Alert>
            ) : null}
            {lifecycleMutation.error ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        {getErrorMessage(
                            lifecycleMutation.error,
                            'Failed to update credential status'
                        )}
                    </AlertDescription>
                </Alert>
            ) : null}

            {detailQuery.isLoading ? (
                <p className="text-muted-foreground">Loading credential policy...</p>
            ) : null}

            {detail !== undefined ? (
                <>
                    <Card>
                        <CardHeader>
                            <CardTitle>Policy</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex items-center gap-2">
                                <span className="font-medium">
                                    {detail.policy.ipx_credential_definition_name}
                                </span>
                                <StatusBadge status={detail.policy.status} />
                            </div>
                            <div className="text-sm text-muted-foreground">
                                {getIpxCredentialPolicySystemDisplayName(detail.policy)}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                Key: {formatIpxCredentialPolicyKey(detail.policy)}
                            </div>
                            <div className="grid gap-3 sm:grid-cols-2">
                                <div className="border rounded p-3">
                                    <div className="text-sm text-muted-foreground">
                                        Organization credential creation
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="mt-2"
                                        disabled={
                                            !detail.capabilities
                                                .can_create_organization_credential ||
                                            isCreatingOrganizationCredential
                                        }
                                        title={
                                            detail.capabilities.can_create_organization_credential
                                                ? 'Create organization credential'
                                                : 'This policy does not allow organization credentials or you do not have permission to create them.'
                                        }
                                        onClick={() => {
                                            setCreatingCredentialScope({
                                                scope_type: IpxCredentialScopeType.ORGANIZATION,
                                            });
                                        }}
                                    >
                                        Create organization credential
                                    </Button>
                                </div>
                                <div className="border rounded p-3">
                                    <div className="text-sm text-muted-foreground">
                                        Personal credential creation
                                    </div>
                                    <div className="font-medium">
                                        {detail.capabilities.can_create_role_assignment_credential
                                            ? 'Available'
                                            : 'Unavailable'}
                                    </div>
                                </div>
                            </div>
                            {isCreatingOrganizationCredential ? (
                                <IpxCredentialForm
                                    fieldDescriptors={detail.field_descriptors}
                                    initialValues={{
                                        account_id: detail.policy.default_account_id,
                                        field_values: {},
                                    }}
                                    saveLabel="Create"
                                    requireCompleteFields
                                    isSaving={createCredentialMutation.isPending}
                                    onSave={(payload) => {
                                        createCredentialMutation.mutate({
                                            scope: {
                                                scope_type: IpxCredentialScopeType.ORGANIZATION,
                                            },
                                            payload,
                                        });
                                    }}
                                    onCancel={() => {
                                        setCreatingCredentialScope(null);
                                    }}
                                />
                            ) : null}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Fields</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {detail.field_descriptors.length === 0 ? (
                                <p className="text-muted-foreground">No fields configured.</p>
                            ) : (
                                <div className="space-y-3">
                                    {detail.field_descriptors.map(renderFieldDescriptor)}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Credentials ({detail.credentials.length})</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {detail.credentials.length === 0 ? (
                                <p className="text-muted-foreground">
                                    No credentials configured for this policy.
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    {detail.credentials.map((credential) => {
                                        const isEditingCredential: boolean =
                                            editingCredentialUuid === credential.credential_uuid;
                                        const roleAssignmentUuid =
                                            getCredentialRoleAssignmentUuid(credential);
                                        const roleAssignmentCredentials =
                                            roleAssignmentUuid === null
                                                ? []
                                                : listCredentialsForRoleAssignment(
                                                      detail.credentials,
                                                      roleAssignmentUuid
                                                  );
                                        const isCreatingCredential: boolean =
                                            roleAssignmentUuid !== null &&
                                            creatingRoleAssignmentUuid === roleAssignmentUuid;
                                        const canCreateAdditionalCredential =
                                            roleAssignmentUuid !== null &&
                                            detail.capabilities
                                                .can_create_role_assignment_credential &&
                                            firstCredentialUuidByRoleAssignment.get(
                                                roleAssignmentUuid
                                            ) === credential.credential_uuid;
                                        return (
                                            <div
                                                key={credential.credential_uuid}
                                                className="border rounded p-4 space-y-3"
                                            >
                                                <div className="flex items-center justify-between gap-3">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-medium">
                                                            {credential.account_id}
                                                        </span>
                                                        <StatusBadge status={credential.status} />
                                                    </div>
                                                    {!isEditingCredential ? (
                                                        <div className="flex items-center gap-2">
                                                            {canCreateAdditionalCredential &&
                                                            !isCreatingCredential ? (
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        if (
                                                                            roleAssignmentUuid ===
                                                                            null
                                                                        ) {
                                                                            return;
                                                                        }
                                                                        setCreatingCredentialScope({
                                                                            scope_type:
                                                                                IpxCredentialScopeType.ROLE_ASSIGNMENT,
                                                                            role_assignment_uuid:
                                                                                roleAssignmentUuid,
                                                                        });
                                                                    }}
                                                                >
                                                                    Create credential
                                                                </Button>
                                                            ) : null}
                                                            {credential.status !==
                                                            Status.DELETED ? (
                                                                <Button
                                                                    type="button"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    disabled={
                                                                        !credential.capabilities
                                                                            .can_edit
                                                                    }
                                                                    title={
                                                                        credential.capabilities
                                                                            .can_edit
                                                                            ? 'Edit credential'
                                                                            : 'You do not have permission to edit this credential.'
                                                                    }
                                                                    onClick={() => {
                                                                        setEditingCredentialUuid(
                                                                            credential.credential_uuid
                                                                        );
                                                                    }}
                                                                >
                                                                    Edit
                                                                </Button>
                                                            ) : null}
                                                            {listCredentialLifecycleActions(
                                                                credential
                                                            ).map((action) => {
                                                                const canRunAction =
                                                                    getCredentialLifecycleCapability(
                                                                        credential,
                                                                        action
                                                                    );
                                                                return (
                                                                    <Button
                                                                        key={action}
                                                                        type="button"
                                                                        variant="outline"
                                                                        size="sm"
                                                                        disabled={
                                                                            !canRunAction ||
                                                                            lifecycleMutation.isPending
                                                                        }
                                                                        title={
                                                                            canRunAction
                                                                                ? `${getCredentialLifecycleButtonLabel(action)} credential`
                                                                                : getCredentialLifecycleDisabledReason(
                                                                                      action
                                                                                  )
                                                                        }
                                                                        onClick={() => {
                                                                            lifecycleMutation.mutate(
                                                                                {
                                                                                    action,
                                                                                    credentialUuid:
                                                                                        credential.credential_uuid,
                                                                                }
                                                                            );
                                                                        }}
                                                                    >
                                                                        {getCredentialLifecycleButtonLabel(
                                                                            action
                                                                        )}
                                                                    </Button>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className="text-sm text-muted-foreground">
                                                    {renderCredentialScope(credential)}
                                                </div>
                                                <div className="text-sm text-muted-foreground">
                                                    {credential.capabilities.can_display
                                                        ? credential.masked_preview
                                                        : 'Credential material hidden'}
                                                </div>
                                                {renderCredentialRotationControls({
                                                    credential,
                                                    organizationUuid,
                                                    policyUuid,
                                                })}
                                                {isEditingCredential ? (
                                                    <IpxCredentialForm
                                                        fieldDescriptors={getCredentialFieldDescriptors(
                                                            credential,
                                                            detail.field_descriptors
                                                        )}
                                                        initialValues={buildCredentialFormInitialValues(
                                                            credential,
                                                            detail.field_descriptors
                                                        )}
                                                        isSaving={
                                                            updateCredentialMutation.isPending
                                                        }
                                                        onSave={(payload) => {
                                                            updateCredentialMutation.mutate({
                                                                credentialUuid:
                                                                    credential.credential_uuid,
                                                                payload,
                                                            });
                                                        }}
                                                        validateSave={(payload) =>
                                                            roleAssignmentUuid === null
                                                                ? undefined
                                                                : validateIpxCredentialAccountId({
                                                                      accountId: payload.account_id,
                                                                      defaultAccountId:
                                                                          detail.policy
                                                                              .default_account_id,
                                                                      credentials:
                                                                          roleAssignmentCredentials,
                                                                      excludedCredentialUuid:
                                                                          credential.credential_uuid,
                                                                  })
                                                        }
                                                        onCancel={() => {
                                                            setEditingCredentialUuid(null);
                                                        }}
                                                    />
                                                ) : (
                                                    renderCredentialFieldDisplayList(
                                                        credential,
                                                        detail.field_descriptors
                                                    )
                                                )}
                                                {isCreatingCredential &&
                                                roleAssignmentUuid !== null ? (
                                                    <IpxCredentialForm
                                                        fieldDescriptors={detail.field_descriptors}
                                                        initialValues={{
                                                            account_id:
                                                                detail.policy.default_account_id,
                                                            field_values: {},
                                                        }}
                                                        saveLabel="Create"
                                                        requireCompleteFields
                                                        isSaving={
                                                            createCredentialMutation.isPending
                                                        }
                                                        onSave={(payload) => {
                                                            createCredentialMutation.mutate({
                                                                scope: {
                                                                    scope_type:
                                                                        IpxCredentialScopeType.ROLE_ASSIGNMENT,
                                                                    role_assignment_uuid:
                                                                        roleAssignmentUuid,
                                                                },
                                                                payload,
                                                            });
                                                        }}
                                                        validateSave={(payload) =>
                                                            validateIpxCredentialAccountId({
                                                                accountId: payload.account_id,
                                                                defaultAccountId:
                                                                    detail.policy
                                                                        .default_account_id,
                                                                credentials:
                                                                    roleAssignmentCredentials,
                                                            })
                                                        }
                                                        onCancel={() => {
                                                            setCreatingCredentialScope(null);
                                                        }}
                                                    />
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>
                                Missing Personal Credentials (
                                {detail.eligible_missing_role_assignments.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {detail.eligible_missing_role_assignments.length === 0 ? (
                                <p className="text-muted-foreground">
                                    No eligible role assignments are missing a credential.
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {detail.eligible_missing_role_assignments.map(
                                        (roleAssignment) => {
                                            const isCreatingCredential: boolean =
                                                creatingRoleAssignmentUuid ===
                                                roleAssignment.role_assignment_uuid;
                                            return (
                                                <div
                                                    key={roleAssignment.role_assignment_uuid}
                                                    className="border rounded p-3 space-y-3"
                                                >
                                                    <div className="flex items-center justify-between gap-3">
                                                        <div className="flex items-center gap-3">
                                                            <Link
                                                                className="text-primary underline underline-offset-2"
                                                                to={`/users/${roleAssignment.user_uuid}`}
                                                            >
                                                                {roleAssignment.label}
                                                            </Link>
                                                            <StatusBadge
                                                                status={
                                                                    roleAssignment.role_assignment_status
                                                                }
                                                            />
                                                        </div>
                                                        {!isCreatingCredential ? (
                                                            <Button
                                                                type="button"
                                                                variant="outline"
                                                                size="sm"
                                                                disabled={
                                                                    !roleAssignment
                                                                        .create_capability
                                                                        .can_create
                                                                }
                                                                title={
                                                                    roleAssignment.create_capability
                                                                        .can_create
                                                                        ? 'Create credential'
                                                                        : (roleAssignment
                                                                              .create_capability
                                                                              .disabled_reason ??
                                                                          'You do not have permission to create this personal credential.')
                                                                }
                                                                onClick={() => {
                                                                    setCreatingCredentialScope({
                                                                        scope_type:
                                                                            IpxCredentialScopeType.ROLE_ASSIGNMENT,
                                                                        role_assignment_uuid:
                                                                            roleAssignment.role_assignment_uuid,
                                                                    });
                                                                }}
                                                            >
                                                                Create credential
                                                            </Button>
                                                        ) : null}
                                                    </div>
                                                    {isCreatingCredential ? (
                                                        <IpxCredentialForm
                                                            fieldDescriptors={
                                                                detail.field_descriptors
                                                            }
                                                            initialValues={{
                                                                account_id:
                                                                    detail.policy
                                                                        .default_account_id,
                                                                field_values: {},
                                                            }}
                                                            saveLabel="Create"
                                                            requireCompleteFields
                                                            isSaving={
                                                                createCredentialMutation.isPending
                                                            }
                                                            onSave={(payload) => {
                                                                createCredentialMutation.mutate({
                                                                    scope: {
                                                                        scope_type:
                                                                            IpxCredentialScopeType.ROLE_ASSIGNMENT,
                                                                        role_assignment_uuid:
                                                                            roleAssignment.role_assignment_uuid,
                                                                    },
                                                                    payload,
                                                                });
                                                            }}
                                                            validateSave={(payload) =>
                                                                validateIpxCredentialAccountId({
                                                                    accountId: payload.account_id,
                                                                    defaultAccountId:
                                                                        detail.policy
                                                                            .default_account_id,
                                                                    credentials:
                                                                        listCredentialsForRoleAssignment(
                                                                            detail.credentials,
                                                                            roleAssignment.role_assignment_uuid
                                                                        ),
                                                                })
                                                            }
                                                            onCancel={() => {
                                                                setCreatingCredentialScope(null);
                                                            }}
                                                        />
                                                    ) : null}
                                                </div>
                                            );
                                        }
                                    )}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </>
            ) : null}
        </div>
    );
}
